import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, AlertTriangle, CheckCircle } from 'lucide-react';
import SEO from '../components/SEO';

const DeleteAccountRequestPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Por favor, informe seu endereço de e-mail');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Aqui você implementaria a lógica para enviar o e-mail para os administradores
      // Por enquanto, vamos apenas simular o sucesso após um breve delay
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Sucesso simulado
      setSuccess(true);
      setEmail('');
      setReason('');
    } catch (err: any) {
      setError(err.message || 'Ocorreu um erro ao enviar sua solicitação. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO 
        title="Solicitar Exclusão de Conta - Boardverso" 
        description="Solicite a exclusão da sua conta e dados pessoais do Boardverso"
      />
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-extrabold text-gray-900 mb-2">Boardverso</h1>
            <h2 className="text-2xl font-bold text-gray-800">Solicitar Exclusão de Conta</h2>
          </div>
          
          <div className="bg-white py-8 px-6 shadow rounded-lg">
            {success ? (
              <div className="text-center">
                <div className="mb-4 flex justify-center">
                  <CheckCircle size={48} className="text-green-500" />
                </div>
                <h3 className="text-xl font-medium text-gray-900 mb-2">Solicitação enviada com sucesso</h3>
                <p className="text-gray-700 mb-6">
                  Recebemos sua solicitação de exclusão de conta. Nossa equipe irá processá-la
                  em até 5 dias úteis e você receberá uma confirmação por e-mail quando o processo
                  for concluído.
                </p>
                <Link 
                  to="/" 
                  className="inline-flex justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Voltar para a página inicial
                </Link>
              </div>
            ) : (
              <>
                <div className="rounded-md bg-yellow-50 p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertTriangle className="h-5 w-5 text-yellow-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">Importante</h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>
                          Se você ainda consegue acessar sua conta, recomendamos fazer login e 
                          excluir sua conta através das configurações do perfil. Este formulário 
                          é destinado apenas a usuários que não conseguem mais acessar o aplicativo.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-6">
                  <p className="text-gray-700">
                    Preencha este formulário para solicitar a exclusão da sua conta no Boardverso. 
                    Após a exclusão, todos os seus dados pessoais serão removidos permanentemente 
                    de nossos sistemas.
                  </p>
                  
                  {error && (
                    <div className="rounded-md bg-red-50 p-4">
                      <div className="flex">
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-red-800">Erro</h3>
                          <div className="mt-2 text-sm text-red-700">
                            <p>{error}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                        E-mail da conta *
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Mail className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="email"
                          id="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                          placeholder="seu.email@exemplo.com"
                          required
                        />
                      </div>
                      <p className="mt-1 text-sm text-gray-500">
                        Informe o e-mail associado à sua conta
                      </p>
                    </div>
                    
                    <div>
                      <label htmlFor="reason" className="block text-sm font-medium text-gray-700">
                        Motivo da exclusão (opcional)
                      </label>
                      <div className="mt-1">
                        <textarea
                          id="reason"
                          rows={3}
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        />
                      </div>
                    </div>
                    
                    <div className="pt-2">
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                      >
                        {loading ? 'Enviando...' : 'Enviar solicitação de exclusão'}
                      </button>
                    </div>
                  </form>
                </div>
              </>
            )}
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600">
              Tem dúvidas? Entre em contato pelo e-mail{' '}
              <a href="mailto:contato@boardverso.com" className="font-medium text-indigo-600 hover:text-indigo-500">
                contato@boardverso.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default DeleteAccountRequestPage; 